<template>
  <div class="batch-timeline" :class="{'is-canceled': status === 'canceled'}">
    <div class="batch-timeline-step" v-for="step in steps" :key="step.key"
         :class="[step.state, {'is-skipped': step.skipped}]">
      <span class="batch-timeline-circle" :class="{tooltip: step.by}" :data-tooltip="step.by">
        <fa-icon :icon="['fal', 'check']" v-if="step.state === 'done'"/>
        <fa-icon :icon="['fal', 'ban']" v-else-if="step.key === 'canceled'"/>
        <template v-else>{{ step.index }}</template>
      </span>
      <span class="batch-timeline-label">{{ step.label }}</span>
      <span class="batch-timeline-date">{{ step.detail }}</span>
    </div>
  </div>
</template>

<script>
import dateFilter from '@/filters/date';

const FLOW = ['closed', 'sent', 'analyzed', 'in_appeal', 'settled'];

const LABELS = {
  closed: 'Fechado',
  sent: 'Enviado',
  analyzed: 'Analisado',
  in_appeal: 'Em recurso',
  settled: 'Baixado',
  canceled: 'Cancelado',
};

const NEXT_DETAILS = {
  sent: 'aguardando envio',
  analyzed: 'aguardando análise',
  in_appeal: 'se houver glosa',
  settled: 'aguardando baixa',
};

export default {
  props: {
    status: {
      type: String,
      required: true,
    },
    dates: {
      type: Object,
      default: () => ({}),
    },
    events: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    hadAppeal() {
      return this.events.some(({ type }) => type === 'appeal_sent');
    },
    appealSentAt() {
      const event = [...this.events].reverse().find(({ type }) => type === 'appeal_sent');
      return event ? event.at : null;
    },
    currentIndex() {
      return FLOW.indexOf(this.status);
    },
    steps() {
      const canceled = this.status === 'canceled';
      const lastDone = canceled ? this.lastReachedIndex() : this.currentIndex;
      const flow = FLOW.map((key, index) => {
        let state = 'future';
        if (index < lastDone) state = 'done';
        else if (index === lastDone) state = canceled ? 'done' : 'current';
        const skipped = key === 'in_appeal' && !this.hadAppeal
          && (this.status === 'settled' || (canceled && lastDone >= 4));
        return {
          key,
          index: index + 1,
          label: LABELS[key],
          state,
          skipped,
          detail: this.detailOf(key, state, skipped),
          by: state === 'done' || state === 'current' ? this.byOf(key) : null,
        };
      });
      if (!canceled) return flow;
      const reached = flow.filter(step => step.state !== 'future');
      return [...reached, {
        key: 'canceled',
        index: reached.length + 1,
        label: LABELS.canceled,
        state: 'canceled',
        skipped: false,
        detail: this.detailOf('canceled', 'canceled', false),
        by: this.byOf('canceled'),
      }];
    },
  },
  methods: {
    lastReachedIndex() {
      const reached = FLOW.filter(key => this.dateOf(key));
      return reached.length > 0 ? FLOW.indexOf(reached[reached.length - 1]) : 0;
    },
    dateOf(key) {
      const map = {
        closed: this.dates.closedAt,
        sent: this.dates.sentAt,
        analyzed: this.dates.analyzedAt || (this.currentIndex >= 2 ? this.dates.receivedAt : null),
        in_appeal: this.appealSentAt,
        settled: this.dates.settledAt,
        canceled: this.dates.canceledAt,
      };
      return map[key] || null;
    },
    byOf(key) {
      const event = [...this.events].reverse()
        .find(({ type }) => type === (key === 'in_appeal' ? 'appeal_sent' : key));
      return event && event.by && event.by.name ? `por ${event.by.name}` : null;
    },
    detailOf(key, state, skipped) {
      if (skipped) return 'sem recurso';
      const date = this.dateOf(key);
      if (date && state !== 'future') return dateFilter(date);
      if (state === 'future' && FLOW.indexOf(key) === this.currentIndex + 1) {
        return NEXT_DETAILS[key] || '';
      }
      return '';
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
$amber-border: #F2C94C;
$lilac-border: lighten($primary-color, 26%);

.batch-timeline {
  border-bottom: $border-color solid $border-width;
  display: flex;
  margin-top: $layout-spacing-lg;
  padding-bottom: $layout-spacing-lg;
  .batch-timeline-step {
    flex: 1;
    position: relative;
    text-align: center;
    &::before {
      background-color: $border-color-dark;
      content: "";
      height: 1px;
      left: 50%;
      position: absolute;
      top: .45rem;
      width: 100%;
    }
    &:last-child::before {
      display: none;
    }
    .batch-timeline-circle {
      align-items: center;
      background-color: $light-color;
      border: $border-color-dark solid $border-width;
      border-radius: 50%;
      color: $gray-color-dark;
      display: inline-flex;
      font-size: .5rem;
      font-weight: 600;
      height: .9rem;
      justify-content: center;
      position: relative;
      width: .9rem;
      z-index: 1;
    }
    .batch-timeline-label {
      color: $gray-color-dark;
      display: block;
      font-size: $font-size-sm;
      margin-top: .15rem;
    }
    .batch-timeline-date {
      color: $gray-color-dark;
      display: block;
      font-size: $font-size-xs;
    }
    &.done {
      &::before {
        background-color: $lilac-border;
      }
      .batch-timeline-circle {
        background-color: lighten($primary-color, 33%);
        border-color: lighten($primary-color, 30%);
        color: $primary-color;
      }
      .batch-timeline-label {
        color: $gray-color-dark;
      }
    }
    &.current {
      .batch-timeline-circle {
        background-color: $light-color;
        border-color: $primary-color;
        color: $primary-color;
      }
      .batch-timeline-label {
        color: $secondary-color-dark;
        font-weight: 600;
      }
    }
    &.canceled {
      .batch-timeline-circle {
        background-color: $error-color;
        border-color: $error-color;
        color: $light-color;
      }
      .batch-timeline-label {
        color: darken($error-color, 8%);
        font-weight: 600;
      }
    }
    &.is-skipped .batch-timeline-label {
      color: $gray-color;
      text-decoration: line-through;
    }
  }
  &.is-canceled .batch-timeline-step.done {
    &::before {
      background-color: $border-color-dark;
    }
    .batch-timeline-circle {
      border-color: $gray-color;
      color: $gray-color;
    }
    .batch-timeline-label {
      color: $gray-color-dark;
    }
  }
  @media screen and (max-width: $size-md) {
    align-items: stretch;
    flex-direction: column;
    .batch-timeline-step {
      align-items: baseline;
      display: flex;
      gap: $layout-spacing;
      padding: .15rem 0;
      text-align: left;
      &::before {
        height: 100%;
        left: .45rem;
        top: 1rem;
        width: 1px;
      }
      .batch-timeline-label {
        margin-top: 0;
      }
    }
  }
}
</style>
